import { useEffect, useState } from 'react';

import moment from 'jalali-moment';
import queryString from 'query-string';
import { Link, useLocation } from 'react-router-dom';

import Loading from '../../Shared/Components/Loading';
import { error } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { submitGatewayForm } from '../../Shared/utils/helper';
import Master from '../Layouts/Master';

const formatRial = (amount) =>
  Number(amount || 0).toLocaleString('fa-IR');

const MembershipPayment = () => {
  const { search } = useLocation();
  const params = queryString.parse(search);
  const sportId = params?.sport_id;
  const returnUrl = params?.return || '';

  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!sportId) {
      setLoading(false);
      return;
    }
    loadStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sportId]);

  const loadStatus = async () => {
    setLoading(true);
    await useApi.get(
      `/membership/status?sport_id=${sportId}`,
      {},
      (res) => {
        const body = res.data;
        if ((body?.status ?? 200) >= 400) {
          error(body?.message || 'خطا در دریافت اطلاعات حق عضویت');
          setLoading(false);
          return;
        }
        setStatus(body?.data);
        setLoading(false);
      },
      (err) => {
        error(
          err?.message
            || err?.data?.message
            || 'خطا در دریافت اطلاعات حق عضویت',
        );
        setLoading(false);
      },
    );
  };

  const handlePayment = async () => {
    setPayLoading(true);
    await useApi.post(
      '/membership/payment',
      {
        sport_id: Number(sportId),
        return_url: returnUrl,
      },
      (res) => {
        if (submitGatewayForm(res.data?.data)) {
          return;
        }
        error('آدرس درگاه پرداخت دریافت نشد');
        setPayLoading(false);
      },
      (err) => {
        error(err?.message || err?.data?.message || 'خطا در اتصال به درگاه پرداخت');
        setPayLoading(false);
      },
    );
  };

  if (loading) {
    return <Loading />;
  }

  if (!sportId) {
    return (
      <Master title='پرداخت حق عضویت'>
        <div className='alert alert-warning f-14'>رشته ورزشی مشخص نیست.</div>
      </Master>
    );
  }

  const expireLabel = status?.expire_at
    ? moment(status.expire_at).locale('fa').format('jYYYY/jMM/jDD')
    : null;

  return (
    <Master title='پرداخت حق عضویت'>
      <div className='d-flex flex-row justify-content-between border-bottom pb-2 my-3'>
        <div className='d-flex flex-row mt-2 align-items-center'>
          <i className='fad fa-id-card-alt f-20 text-secondary mx-2'></i>
          <h1 className='text-secondary f-13 font-weight-bold mt-1 mb-0 p-0'>
            تمدید حق عضویت
          </h1>
        </div>
        {returnUrl && (
          <Link to={returnUrl} className='btn btn-outline-secondary f-13'>
            بازگشت
          </Link>
        )}
      </div>

      <div className='row justify-content-center my-4'>
        <div className='col-12 col-lg-8'>
          <div
            className='rounded shadow-sm overflow-hidden'
            style={{ background: 'linear-gradient(135deg, #1f8f6a 0%, #33b77a 55%, #b1dae2 100%)' }}
          >
            <div className='p-4 text-white'>
              <div className='d-flex flex-row justify-content-between align-items-start'>
                <div>
                  <div className='f-12 opacity-75 mb-1'>رشته ورزشی</div>
                  <div className='f-20 font-weight-bold'>
                    {status?.sport_name || '—'}
                  </div>
                  <div className='f-13 mt-2 opacity-90'>
                    {status?.membership_title}
                  </div>
                </div>
                <div
                  className='rounded-circle d-flex align-items-center justify-content-center'
                  style={{
                    width: 64,
                    height: 64,
                    background: 'rgba(255,255,255,0.18)',
                  }}
                >
                  <i className='fad fa-medal f-28'></i>
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white rounded shadow-sm p-4 mt-3'>
            {status?.exempt ? (
              <div className='alert alert-success f-14 mb-0'>
                شما از پرداخت حق عضویت معاف هستید.
              </div>
            ) : status?.active ? (
              <div className='alert alert-success f-14 mb-0'>
                حق عضویت شما برای این رشته فعال است
                {expireLabel ? ` تا ${expireLabel}` : ''}.
              </div>
            ) : (
              <>
                <div className='row'>
                  <div className='col-12 col-md-6 mb-3'>
                    <div className='border rounded p-3 h-100 bg-sky-light'>
                      <div className='text-secondary f-12 mb-1'>مدت اعتبار</div>
                      <div className='f-16 font-weight-bold text-dark'>
                        {status?.duration_day
                          ? `${Number(status.duration_day).toLocaleString('fa-IR')} روز`
                          : '—'}
                      </div>
                    </div>
                  </div>
                  <div className='col-12 col-md-6 mb-3'>
                    <div className='border rounded p-3 h-100 bg-sky-light'>
                      <div className='text-secondary f-12 mb-1'>مبلغ قابل پرداخت</div>
                      <div className='f-18 font-weight-bold text-dark'>
                        {formatRial(status?.price)} <span className='f-12'>ریال</span>
                      </div>
                    </div>
                  </div>
                </div>

                {expireLabel && (
                  <div className='alert alert-warning f-13'>
                    آخرین انقضای ثبت‌شده: {expireLabel} — با پرداخت موفق، اعتبار تمدید می‌شود.
                  </div>
                )}

                <div className='border rounded p-3 mb-3'>
                  <div className='d-flex flex-row align-items-center mb-2'>
                    <i className='fad fa-info-circle text-info ml-2'></i>
                    <span className='f-13 text-secondary'>
                      پرداخت از درگاه بانکی امن انجام می‌شود و پس از تأیید، حق عضویت شما در همان لحظه فعال خواهد شد.
                    </span>
                  </div>
                </div>

                <div className='d-flex flex-row justify-content-center'>
                  <button
                    type='button'
                    className='btn btn-info f-15 px-5 py-2 shadow-sm'
                    disabled={payLoading}
                    onClick={handlePayment}
                  >
                    {payLoading ? (
                      <div
                        className='spinner-border spinner-border-sm text-white'
                        role='status'
                      ></div>
                    ) : (
                      <>
                        <i className='fad fa-credit-card ml-2'></i>
                        پرداخت حق عضویت
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Master>
  );
};

export default MembershipPayment;
